import { useRef, useState, useCallback } from "react";
import { useSiteContent } from "@/context/SiteContentContext";
import { Reveal, Overline } from "@/components/site/Reveal";

export default function BeforeAfter() {
  const { compareBefore, compareAfter } = useSiteContent();
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    p = Math.max(0, Math.min(100, p));
    setPos(p);
  }, []);

  const onDown = (e) => {
    dragging.current = true;
    setFromClientX(e.clientX ?? e.touches?.[0]?.clientX);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    setFromClientX(e.clientX ?? e.touches?.[0]?.clientX);
  };
  const onUp = () => {
    dragging.current = false;
  };

  return (
    <section
      data-testid="before-after-section"
      className="relative bg-charcoal py-24 lg:py-36"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <Reveal>
            <Overline className="mb-6 justify-center [&>span:first-child]:hidden">
              The Transformation
            </Overline>
          </Reveal>
          <Reveal delay={0.06}>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream uppercase tracking-tight">
              See The <span className="gold-text italic lowercase">difference.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-cream/55 font-light">
              From overlooked to exceptional. Drag to reveal.
            </p>
          </Reveal>
        </div>

        <Reveal y={40}>
          <div
            ref={containerRef}
            data-testid="before-after-comparison-slider"
            className="relative w-full aspect-[16/9] max-h-[640px] overflow-hidden select-none cursor-ew-resize border border-white/5"
            onMouseDown={onDown}
            onMouseMove={onMove}
            onMouseUp={onUp}
            onMouseLeave={onUp}
            onTouchStart={onDown}
            onTouchMove={onMove}
            onTouchEnd={onUp}
          >
            {/* After (full colour) */}
            <img
              src={compareAfter}
              alt="After detailing"
              className="absolute inset-0 h-full w-full object-cover"
              draggable={false}
            />
            <span className="absolute top-5 right-5 z-20 font-mono text-[10px] tracking-[0.25em] text-gold bg-ink/60 px-3 py-1.5 backdrop-blur">
              AFTER
            </span>

            {/* Before (dulled) */}
            <div
              className="absolute inset-0 overflow-hidden"
              style={{ width: `${pos}%` }}
            >
              <img
                src={compareBefore}
                alt="Before detailing"
                className="absolute inset-0 h-full w-full object-cover"
                style={{
                  width: containerRef.current
                    ? containerRef.current.offsetWidth
                    : "100%",
                  maxWidth: "none",
                  filter:
                    compareBefore === compareAfter
                      ? "grayscale(0.85) brightness(0.5) contrast(0.9)"
                      : "none",
                }}
                draggable={false}
              />
              <span className="absolute top-5 left-5 z-20 font-mono text-[10px] tracking-[0.25em] text-cream/80 bg-ink/60 px-3 py-1.5 backdrop-blur">
                BEFORE
              </span>
            </div>

            {/* Handle */}
            <div
              className="absolute top-0 bottom-0 z-30 w-px bg-gold"
              style={{ left: `${pos}%` }}
            >
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-12 w-12 rounded-full bg-gold text-ink flex items-center justify-center shadow-[0_0_30px_rgba(201,162,39,0.5)]">
                <span className="text-xs font-mono tracking-tighter">◄ ►</span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
